<template>
  <div class="pa-3 flex">
    <div class="title mb-3">
      Controls
    </div>
    <div>
      <section
        id="filters"
        class="mb-3"
      >
        <v-select
          v-model="filters.application.value"
          v-focus
          :items="applications"
          hide-details
          single-line
          class="mt-0 pt-0 ml-4 select-mode"
          :disabled="filters.application.disabled"
          @input="onApplicationChange(false)"
        />
        <v-select
          v-if="filters.team.displayed"
          v-model="filters.team.value"
          v-focus
          :items="teams"
          hide-details
          single-line
          class="mt-0 pt-0 ml-4 select-mode"
          :disabled="filters.team.disabled"
          item-text="name"
          item-value="id"
          @input="onTeamChange"
        />
      </section>
      <section id="stats">
        <div class="stat">
          <div class="figure">
            {{ issues.total }}
          </div>
          <div class="subtitle">
            problem{{ issues.total > 1 ? "s" : "" }} since {{ sinceTxt }}
          </div>
          <div
            v-if="issues.with_accidents > 0"
            class="sub-footer"
            title="Some of these problems imply an accident"
          >
            <v-icon
              class="alert"
              size="x-large"
            >
              mdi-bell-alert
            </v-icon><span class="badge">{{ issues.with_accidents }}</span>
          </div>
        </div>
        <div class="stat">
          <div class="figure">
            {{ toControls }}
          </div>
          <div class="subtitle">
            session{{ toControls > 1 ? "s" : "" }} to control
          </div>
        </div>
        <div class="stat">
          <div class="figure">
            {{ toEvaluate }}
          </div>
          <div class="subtitle">
            session{{ toEvaluate > 1 ? "s" : "" }} to evaluate
          </div>
          <div
            v-if="toEvaluate > 0"
            class="sub-footer centered"
          >
            <router-link
              :to="{
                name: 'sessions-index',
                query: {
                  from: 'controls',
                  application: filters.application.value,
                  team: filters.team.value,
                },
              }"
            >
              Accéder aux sessions concernées
            </router-link>
          </div>
        </div>
      </section>
      <section
        id="to-control"
        class="mt-4"
      >
        <h2>List of sessions</h2>
        <div class="sub-filters">
          <v-text-field
            v-model="filters.dates.start_text"
            label="Start date"
            outline
            hide-details
            :clearable="true"
            class="date"
            @blur="filters.dates.start = parseDate(filters.dates.start_text)"
            @change="onListFiltersChange"
            @click:clear="clearDate('start')"
          >
            <div slot="prepend-inner">
              <v-menu
                ref="searchStart"
                :close-on-content-click="false"
                :return-value.sync="filters.dates.start"
                transition="scale-transition"
                offset-y
                full-width
                lazy
              >
                <v-icon slot="activator">
                  event
                </v-icon>
                <v-date-picker
                  v-model="filters.dates.start"
                  first-day-of-week="1"
                  @change="onListFiltersChange"
                  @input="
                    $refs.searchStart.save(filters.dates.start);
                    filters.dates.start_text = americanizeDate(
                      filters.dates.start
                    );
                    updateEnd();
                  "
                />
              </v-menu>
            </div>
          </v-text-field>
          <v-text-field
            v-model="filters.dates.end_text"
            label="End date"
            outline
            hide-details
            :clearable="true"
            class="date"
            @blur="filters.dates.end = parseDate(filters.dates.end_text)"
            @change="onListFiltersChange"
            @click:clear="clearDate('start')"
          >
            <div slot="prepend-inner">
              <v-menu
                ref="searchEnd"
                :close-on-content-click="false"
                :return-value.sync="filters.dates.end"
                transition="scale-transition"
                offset-y
                full-width
              >
                <v-icon slot="activator">
                  event
                </v-icon>
                <v-date-picker
                  ref="endPicker"
                  v-model="filters.dates.end"
                  first-day-of-week="1"
                  @change="onListFiltersChange"
                  @input="
                    $refs.searchEnd.save(filters.dates.end);
                    filters.dates.end_text = americanizeDate(filters.dates.end);
                  "
                />
              </v-menu>
            </div>
          </v-text-field>
          <v-autocomplete
            v-model="filters.topic_id"
            v-focus
            label="Concept"
            hide-details
            multiple
            outline
            small-chips
            deletable-chips
            :items="topics"
            item-value="id"
            item-text="name"
            dense
            :clearable="true"
            @change="onListFiltersChange"
          >
            <template
              slot="selection"
              slot-scope="{ index }"
            >
              <span
                v-if="index === 0"
                class="grey--text caption"
              >{{ filters.topic_id.length }} selected</span>
            </template>
          </v-autocomplete>
          <v-switch
            v-model="filters.includes_to_evaluate"
            label="Include sessions to evaluate ?"
            class="switch"
            color="primary"
            @change="onListFiltersChange"
          />
          <v-switch
            v-model="filters.includes_controlled"
            label="Include controlled sessions ?"
            class="switch"
            color="primary"
            @change="onListFiltersChange"
          />
          <a @click="resetSubFilters">Reset filters</a>
        </div>
        <div id="list">
          <sessions-list
            v-if="fullyLoaded"
            ref="sessionsList"
            :application="filters.application.value"
            :team="filters.team.value"
            :dates="filters.dates"
            :topic="filters.topic_id"
            :includes-to-evaluate="filters.includes_to_evaluate"
            :includes-controlled="filters.includes_controlled"
          />
        </div>
      </section>
    </div>
  </div>
</template>
<script>
import moment from "moment";
import Auth from "@/plugins/auth";
import Consts from "@/consts";
import GlobalMixin from "@/mixins/global";
import Params from "@/api/params";
import Sessions from "@/api/sessions";
import SessionsList from "./Sessions.vue";

export default {
  components: { SessionsList },
  mixins: [GlobalMixin],
  data() {
    return {
      issues: {
        total: 0,
        with_accidents: 0,
        since: 1, //day(s)
      },
      toEvaluate: 12,
      toControls: 0,
      teams: [],
      allTeams: [],
      filters: {
        application: {
          value: Consts.APPLICATION.ALL,
          disabled: false,
        },
        team: {
          value: 0, //Toutes
          displayed: true,
          disabled: false,
        },
        dates: {
          start: null,
          start_text: "",
          end: null,
          end_text: "",
        },
        topic_id: [],
        includes_to_evaluate: true,
        includes_controlled: false,
      },
      topics: [],
      fullyLoaded: false,
    };
  },
  computed: {
    sinceTxt() {
      return this.issues.since && this.issues.since > 1
        ? `${this.issues.since} days`
        : "yesterday";
    },
  },
  async mounted() {
    const { data } = await Params.getList("teams", {
      is_enabled: 1,
      sortBy: "name",
    });
    this.allTeams = data;
    this.allTeams.unshift({ id: 0, name: "All" });

    //on doit récupérer des infos sur l'utilisateur pour connaître la nature des filtres auxquels il aura accès
    this.filters.application.value = Auth.user.application;
    this.filters.application.disabled =
      !Auth.user.is_admin && this.filters.application.value > 0;

    //on va tout de même checker ce qu'on a dans le store
    const app = this.getStoredApp();
    if (Auth.user.application == Consts.APPLICATION.ALL && app) {
      this.filters.application.value = app;
    }
    const storedFilters = this.getStoredObject("controls.sub_filters");
    if (storedFilters) {
      this.filters = Object.assign(this.filters, storedFilters);
    }
    this.onApplicationChange(true);
    this.fetch();
  },
  methods: {
    async fetch() {
      //les DaggesrapportProblems
      const { data: problems } = await Sessions.getNbSessionsWithIssues(
        this.filters.application.value,
        this.filters.team.value
      );
      this.issues = Object.assign(this.issues, problems);
      //le nombre de sessions à évaluer
      const { data: to_evaluate } = await Sessions.getNbSessionsToEvaluate(
        this.filters.application.value,
        this.filters.team.value
      );
      this.toEvaluate = to_evaluate.total;
      //le nombre de sessions à contrôler
      const { data: to_control } = await Sessions.getNbSessionsToControl(
        this.filters.application.value,
        this.filters.team.value
      );
      this.toControls = to_control.total;
      this.fetchTopics();
      this.fullyLoaded = true;
    },
    async fetchTopics() {
      const { data } = await Params.getList("topics", {
        is_enabled: 1,
        sortBy: "name",
        application: this.filters.application.value,
        team_id: this.filters.team.value,
      });
      this.topics = data;
    },
    onApplicationChange(fromMounted = false) {
      if (this.filters.application.value == Consts.APPLICATION.FREELANCES) {
        if(Auth.user.is_admin){
          this.teams = this.allTeams
        } else {
          this.teams = Auth.user.teams
        }
        
        this.filters.team.displayed = true;
        const storedTeam = this.getStoredTeam();
        if (Auth.user.teams && Auth.user.teams.length) {
          this.filters.team.value =
            storedTeam && storedTeam != 0
              ? storedTeam
              : Auth.user.teams[0].id;
          if (!Auth.user.is_admin && Auth.user.teams.length == 1) {
            this.filters.team.disabled = true; // en gros on l'affiche mais on le disable parce que le mec n'est pas admin donc il ne peut voir que ça
          }
        } else {
          this.filters.team.value =
            storedTeam && storedTeam != 0 ? storedTeam : 0;
        }
      } else {
        this.filters.team.displayed = false;
        this.filters.team.value = 0;
        this.teams = this.allTeams
      }
      this.setStoredApp(this.filters.application.value);
      this.setStoredTeam(this.filters.team.value);

      if (!fromMounted) {
        this.filters.topic_id.splice(0);
        this.fetchTopics();
        this.fetch();
        this.refreshSessions(true);
      }
    },
    onTeamChange() {
      this.setStoredTeam(this.filters.team.value);
      this.fetch();
      this.filters.topic_id.splice(0);
      this.onListFiltersChange();
    },
    parseDate(date) {
      if (!date) return null;

      if (date.indexOf("-") >= 0) {
        return date;
      }

      const parsed = moment(date, "DD.MM.YYYY").format("YYYY-MM-DD");
      return parsed != "Invalid date" ? parsed : null;
    },
    americanizeDate(date) {
      if (!date) return null;

      if (date.indexOf(".") >= 0) {
        return date;
      }

      const americanized = moment(date, "YYYY-MM-DD").format("DD.MM.YYYY");
      return americanized != "Invalid date" ? americanized : null;
    },
    updateEnd() {
      this.$refs.endPicker.monthClick(this.filters.dates.start);
    },
    onListFiltersChange() {
      // eslint-disable-next-line no-unused-vars
      const { application, team, ...subfilters } = this.filters;
      this.setStoredObject("controls.sub_filters", subfilters);
      this.refreshSessions(true);
    },
    clearDate(date) {
      this.filters.dates[date] = null;
      this.filters.dates[`${date}_text`] = "";
      this.onListFiltersChange();
    },
    resetSubFilters() {
      this.filters.dates.start = null;
      this.filters.dates.start_text = "";
      this.filters.dates.end = null;
      this.filters.dates.end_text = "";
      this.filters.topic_id.splice(0);
      this.filters.includes_to_evaluate = true;
      this.filters.includes_controlled = false;
      this.onListFiltersChange();
    },
    /**
     * Au départ j'utilisais un :key avec la dernière date de filtrage
     * pour forcer l'actualisation des sessions
     * mais ça faisait plein de merdes, en particulier des double-appels à l'API
     * Solution : appeler le refresh du fils par le parent
     * le nextTick est important, sinon le fils n'a pas le temps de s'actualiser au niveau des props
     */
    refreshSessions(filtered = false) {
      this.$nextTick(() => {
        this.$refs.sessionsList &&
          this.$refs.sessionsList.fetch(filtered ? "filters" : "local");
      });
    },
  },
};
</script>
<style scoped lang="sass">
#filters
  display: flex
  flex-direction: row
  align-items: center
  justify-content: start

  .v-input
    width: 250px
    flex: 0 0 auto !important

#stats
  display: flex
  flex-direction: row
  justify-content: space-around
  flex-wrap: wrap

  .stat
    width: 25%
    min-width: 250px
    display: flex
    flex-direction: column
    background-color: white
    padding: 10px
    margin-top: 10px

    .figure
      font-weight: bold
      color: #f4b944
      font-size: 64px
      text-align: center

    .subtitle
      text-align: center
      color: #333333
      font-size: 1.1rem

    .sub-footer
      display: flex
      flex-direction: row
      justify-content: flex-end

      &.centered
        justify-content: center

      .alert
        margin-right: 5px
        width: 40px
        height: 40px
        font-size: 40px !important
        color: tomato

      .badge
        background-color: tomato
        color: white
        font-weight: bold
        display: flex
        font-size: 0.85rem
        justify-content: center
        align-items: center
        padding: 1px 6px
        border-radius: 25px
        align-self: flex-start

.sub-filters
  padding: 10px
  display: flex
  flex-direction: row
  justify-content: flex-start
  align-items: center
  flex-wrap: wrap

  > *
    width: 350px
    margin-right: 10px

    &.date
      width: 200px
    &.switch
      width: 270px
  a
    text-decoration: underline
</style>
